import React from 'react';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import WarningIcon from '@mui/icons-material/Warning';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CelebrationIcon from '@mui/icons-material/Celebration';
import CallMergeIcon from '@mui/icons-material/CallMerge';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import Chip from '../Chip';
import { PostStatuses } from '../../../Utils/Types';
import { Cancel } from '@mui/icons-material';
import CustomIcon from '../CustomIcon';
import { Icons } from '../../../Themes';

const ChipStyled = styled(Chip)(({ theme }: any) => ({
  svg: {
    marginLeft: theme.spacing(1.5),
  },
}));

// Helper function to get chip properties based on status keyword
// Exported so it can be reused by StatusBadgeMenu
export const getChipsProps = (statusKeyword: string, color = 'white') => {
  if (!statusKeyword) return {};

  switch (statusKeyword) {
    case PostStatuses.TO_MODERATE:
      // À modérer
      return {
        variant: 'contained',
        color: 'default',
        icon: <WarningIcon fontSize="small" />,
      };
    case PostStatuses.REJECTED:
      // Refusée
      return {
        color: 'default',
        variant: 'contained', // Dark gray contained
        icon: <DeleteOutlineIcon fontSize="small" />,
      };
    case PostStatuses.TODO:
      // Déposée
      return {
        variant: 'contained',
        color: 'default',
        icon: <LightbulbIcon fontSize="small" />,
      };
    case PostStatuses.ANSWERED:
      // Non retenue
      return {
        variant: 'contained',
        color: 'default',
        icon: <Cancel fontSize="small" />,
      };
    case PostStatuses.DUPLICATE:
      // Regroupée
      return {
        variant: 'outlined',
        color: 'default',
        icon: <CallMergeIcon fontSize="small" />,
      };
    case PostStatuses.SELECTED:
      // Retenue
      return {
        variant: 'contained',
        color: 'secondary',
        icon: <CustomIcon src={Icons.posts.status.concretized} size={16} color={color} />,
      };
    case PostStatuses.DOING:
      // Analyse en cours
      return {
        color: 'primary',
        variant: 'outlined', // Primary not contained
        icon: <RefreshIcon fontSize="small" />,
      };
    case PostStatuses.IN_PROGRESS:
      // Réalisation en cours
      return {
        color: 'primary',
        variant: 'outlined',
        icon: <RefreshIcon fontSize="small" />,
      };
    case PostStatuses.VALIDATED:
      // Lauréate
      return {
        color: 'secondary',
        variant: 'contained',
        icon: <CheckCircleIcon fontSize="small" />,
      };
    case PostStatuses.DONE:
      // Concrétisée
      return {
        color: 'primary',
        variant: 'contained', // Primary contained
        icon: <CelebrationIcon fontSize="small" />,
      };
    default:
      // Pour tout autre status non répertorié, utiliser un style par défaut
      return {
        variant: 'outlined',
        color: 'default',
        icon: null,
      };
  }
};

// Get just the icon for a status
export const getStatusIcon = (statusKeyword: string, color: string) => {
  const props = getChipsProps(statusKeyword, color);
  return props.icon || null;
};

function StatusBadge({ status, onClick, size = 'small', endIcon = null, ...props }: any) {
  if (!status) {
    return null;
  }

  return (
    <ChipStyled
      label={status.title}
      onClick={onClick}
      clickable={!!onClick}
      size={size}
      deleteIcon={endIcon}
      onDelete={endIcon ? () => {} : undefined}
      {...getChipsProps(status.keyword)}
      {...props}
    />
  );
}

StatusBadge.propTypes = {
  size: PropTypes.oneOf(['medium', 'small']),
  status: PropTypes.shape({
    content: PropTypes.string,
    keyword: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
  }).isRequired,
  onClick: PropTypes.func,
  typographyComponent: PropTypes.string,
  endIcon: PropTypes.element,
};

export default StatusBadge;
